import { supabase } from './supabaseClient';
import { formatAnswersHumanLines } from './diagnosticAnswerLabels';

/**
 * Saves survey answers + computed report. Contact fields are optional.
 * @returns {{ ok: true } | { ok: false, error: string, code?: string }}
 */
export async function saveDiagnosticSubmission({ firstName, lastName, email, answers, report }) {
  if (!supabase) {
    return { ok: false, error: 'Supabase is not configured', code: 'not_configured' };
  }

  const payload = {
    first_name: firstName?.trim() || null,
    last_name: lastName?.trim() || null,
    email: email?.trim() || null,
    answers,
    report,
  };

  const { error } = await supabase.from('diagnostic_submissions').insert(payload);

  if (error) {
    return { ok: false, error: error.message, code: error.code };
  }

  return { ok: true };
}

export function buildDiagnosticDownloadText({ answers, report }) {
  const lines = [];
  lines.push('Cure8 — Lead leak diagnostic summary');
  lines.push(`Generated: ${new Date().toISOString()}`);
  lines.push('');
  lines.push(`Estimated monthly leakage: $${report.monthlyLeakage?.toLocaleString?.('en-CA') ?? report.monthlyLeakage}`);
  lines.push(`Estimated annual: $${report.annualLeakage?.toLocaleString?.('en-CA') ?? report.annualLeakage}`);
  lines.push(`Recoverable (estimate): ~$${report.recoverable?.toLocaleString?.('en-CA') ?? report.recoverable}/mo`);
  if (report.teamLabel) {
    lines.push(`Team size context: ${report.teamLabel}`);
  }
  lines.push(`Recommended start: ${report.recommended === 'pro' ? 'The Pro' : 'The Pilot'}`);
  lines.push('');
  lines.push('Where it is coming from:');
  (report.gaps || []).forEach((g) => {
    lines.push(`- ${g.title} (~$${g.cost}/mo)`);
    lines.push(`  Fix: ${g.fix}`);
  });
  lines.push('');
  lines.push('Your answers:');
  formatAnswersHumanLines(answers || {}).forEach((line) => lines.push(line));
  return lines.join('\n');
}

export function downloadDiagnosticFile({ answers, report, firstName, lastName }) {
  const text = buildDiagnosticDownloadText({ answers, report });
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const safe = [firstName, lastName].filter(Boolean).join('-').replace(/\s+/g, '-') || 'diagnostic';
  const filename = `cure8-diagnostic-${safe}-${Date.now()}.txt`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
